import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import NewsletterSignup from '@/components/NewsletterSignup';
import SocialLinks from '@/components/SocialLinks';
import GoogleReviewsBadge from '@/components/GoogleReviewsBadge';
import { useBrandAsset } from '@/lib/BrandAssetsContext';

export default function Footer() {
  const brandLogo = useBrandAsset('brand_logo');
  return (
    <footer className="bg-[#0a0a0c] border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-12">

          {/* Brand */}
          <div className="md:col-span-4">
            <div className="mb-5">
              <img
                src={brandLogo || "https://media.base44.com/images/public/69e7659143303e8153678f84/d1f090528_Asset1.png"}
                alt="Motion Reality Sim Racing"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-white/40 text-sm leading-relaxed max-w-xs mb-6">
              Premium simulation hardware crafted for the most realistic and immersive racing experience possible.
            </p>
            <div className="space-y-2.5">
              <a href="tel:01515283001" className="flex items-center gap-2.5 text-sm text-white/40 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-primary" /> 0151 528 3001
              </a>
              <a href="mailto:sales@motionreality.co.uk" className="flex items-center gap-2.5 text-sm text-white/40 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-primary" /> sales@motionreality.co.uk
              </a>
              <div className="flex items-start gap-2.5 text-sm text-white/40">
                <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                Unit 9, Croft Business Park, Carrock Road, Bromborough, CH62 3RA
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-white/5">
              <p className="text-white/35 font-bold tracking-widest uppercase text-[10px] mb-1.5">Registered Office</p>
              <p className="text-xs text-white/30 leading-relaxed">
                3rd Floor, 86-90 Paul Street,<br />London, England, EC2A 4NE
              </p>
              <p className="text-xs text-white/25 mt-2">Company No: 16171282 · VAT No: 491 158 870</p>
            </div>
            <div className="mt-6">
              <p className="text-white font-bold text-xs tracking-widest uppercase mb-3">FOLLOW US</p>
              <SocialLinks />
            </div>
          </div>

          {/* Links */}
          <div className="md:col-span-2">
            <h2 className="text-white font-bold text-xs tracking-widest uppercase mb-5">NAVIGATE</h2>
            <div className="space-y-3">
              {[
                { label: 'Home', path: '/' },
                { label: 'Shop', path: '/shop' },
                { label: 'Book a Session', path: '/bookings' },
                { label: 'Gift Cards', path: '/gift-cards' },
                { label: 'About', path: '/about' },
                { label: 'Contact', path: '/contact' },
                { label: 'Customer Service', path: '/customer-service' },
                { label: 'FAQ', path: '/faq' },
                { label: 'My Account', path: '/account' },
              ].map((l) => (
                <Link key={l.path} to={l.path} className="block text-sm text-white/40 hover:text-white transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Legal */}
          <div className="md:col-span-2">
            <h2 className="text-white font-bold text-xs tracking-widest uppercase mb-5">LEGAL</h2>
            <div className="space-y-3">
              {[
                { label: 'Terms & Conditions', path: '/terms-conditions' },
                { label: 'Refund Policy', path: '/refund-policy' },
                { label: 'Returns (RMA)', path: '/returns' },
                { label: 'Delivery Information', path: '/delivery' },
                { label: 'Privacy Policy', path: '/privacy-policy' },
              ].map((l) => (
                <Link key={l.path} to={l.path} className="block text-sm text-white/40 hover:text-white transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Newsletter */}
          <div className="md:col-span-4">
            <h2 className="text-white font-bold text-xs tracking-widest uppercase mb-5">NEWSLETTER</h2>
            <p className="text-white/40 text-sm mb-5">Product launches & booking offers, straight to your inbox.</p>
            <NewsletterSignup variant="footer" />
          </div>
        </div>

        <div className="border-t border-white/5 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/20">© {new Date().getFullYear()} Motion Reality Sim Racing Ltd · Company No: 16171282</p>
          <p className="text-xs text-white/20">Next Day Delivery · Order Before 2pm · UK Mainland</p>
        </div>
      </div>
      <GoogleReviewsBadge />
    </footer>
  );
}